import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useSpecificParams } from 'record-it-components/hooks/useSpecificParams/useSpecificParams';
import {
  MessageBox,
  MessageBoxType
} from 'record-it-components/misc/MessageBox/MessageBox';
import { PageContentContainer } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useEntityByIdOrOriginalId } from '../../../hooks/entity/useEntityByIdOrOriginalId';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { TrackedWorkTimeLine } from '../../timeTracking/TrackedWorkTimeLine/TrackedWorkTimeLine';
import { WorkTimeEntryActivityWidget } from '../../workTime/WorkTimeEntryActivityWidget/WorkTimeEntryActivityWidget';
import { WorkTimeEntryNoteWidget } from '../../workTime/WorkTimeEntryNoteWidget/WorkTimeEntryNoteWidget';

export type EditWorkTimeEntryPageProps = PropsWithoutChildren<
  Record<never, never>
>;

export const EditWorkTimeEntryPage: FunctionComponent<EditWorkTimeEntryPageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    const { workTimeEntryId } = useSpecificParams({
      requiredParamNames: ['workTimeEntryId']
    });

    useConfigureBreadcrumbsWithBuilder((builder) =>
      builder
        .addHomePage()
        .addMyWorkTimeOverviewPage()
        .addEditWorkTimeEntryPage(workTimeEntryId)
    );

    const workTimeEntry = useEntityByIdOrOriginalId(
      EntityName.WorkTimeEntry,
      workTimeEntryId
    );
    const { t } = useTranslation();

    return (
      <PageContentContainer>
        <PageContentHeader
          title={t('components.page.EditWorkTimeEntryPage.title')}
          iconConfig={
            iconConfigurations.prefixed.page.EditWorkTimeEntryPage.titleIcon
          }
        ></PageContentHeader>
        {workTimeEntry ? (
          <>
            <FormGroup>
              <WorkTimeEntryActivityWidget
                workTimeEntry={workTimeEntry}
              ></WorkTimeEntryActivityWidget>

              <WorkTimeEntryNoteWidget
                workTimeEntry={workTimeEntry}
              ></WorkTimeEntryNoteWidget>
            </FormGroup>

            <TrackedWorkTimeLine
              startDate={workTimeEntry.startDate}
              endDate={workTimeEntry.endDate}
              workTimeEntryBreaks={workTimeEntry.breaks}
              data-testid="EditWorkTimeEntryPage.TrackedWorkTimeLine"
            ></TrackedWorkTimeLine>
          </>
        ) : (
          <MessageBox
            type={MessageBoxType.Error}
            data-testid="EditWorkTimeEntryPage.EntryNotFoundMessageBox"
          >
            {t('components.page.EditWorkTimeEntryPage.entryNotFound')}
          </MessageBox>
        )}
      </PageContentContainer>
    );
  });
